/**
 * SoleadLab 2.0 — Truth Qualification Store
 *
 * Zustand store for site truth qualification data.
 * All values are backend outputs — this store never computes truth.
 */

import { create } from 'zustand';
import type { SiteTruthRow, ConclusionFamily } from '@/types/truth';
import * as truthApi from '@/services/truthApi';

// ─── Constants ───────────────────────────────────────────────────────────────

const FAMILIES: ConclusionFamily[] = ['performance', 'condition', 'risk'];

// ─── Store interface ─────────────────────────────────────────────────────────

interface TruthStore {
  currentSiteId: string | null;
  truthByFamily: Record<ConclusionFamily, SiteTruthRow | null>;
  loading: boolean;
  error: string | null;
  recomputeStatus: 'idle' | 'running' | 'done' | 'error';

  setSiteId: (siteId: string | null) => void;
  loadAllFamilies: () => Promise<void>;
  recompute: (family: ConclusionFamily | 'all') => Promise<void>;
}

// ─── Store implementation ────────────────────────────────────────────────────

export const useTruthStore = create<TruthStore>((set, get) => ({
  currentSiteId: null,
  truthByFamily: { performance: null, condition: null, risk: null },
  loading: false,
  error: null,
  recomputeStatus: 'idle',

  setSiteId: (siteId: string | null) => {
    const prev = get().currentSiteId;
    if (siteId === prev) return;
    set({
      currentSiteId: siteId,
      truthByFamily: { performance: null, condition: null, risk: null },
      error: null,
      recomputeStatus: 'idle',
    });
    if (siteId) {
      get().loadAllFamilies();
    }
  },

  loadAllFamilies: async () => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ loading: true, error: null });
    try {
      const results = await Promise.allSettled(
        FAMILIES.map(f => truthApi.getTruthLatest(currentSiteId, f))
      );

      const truthByFamily: Record<ConclusionFamily, SiteTruthRow | null> = {
        performance: null,
        condition: null,
        risk: null,
      };

      for (let i = 0; i < FAMILIES.length; i++) {
        const result = results[i];
        if (result.status === 'fulfilled') {
          truthByFamily[FAMILIES[i]] = result.value.truth_qualification;
        }
        // 404 = no truth record yet — leave as null
      }

      set({ truthByFamily, loading: false });
    } catch (err) {
      console.error('Failed to load truth qualifications:', err);
      set({ loading: false, error: 'Failed to load truth qualifications' });
    }
  },

  recompute: async (family: ConclusionFamily | 'all') => {
    const { currentSiteId } = get();
    if (!currentSiteId) return;

    set({ recomputeStatus: 'running' });
    try {
      await truthApi.triggerTruthRecompute(currentSiteId, family);
      set({ recomputeStatus: 'done' });
      // Refresh after recompute
      get().loadAllFamilies();
    } catch (err) {
      console.error('Truth recompute failed:', err);
      set({ recomputeStatus: 'error' });
    }
  },
}));
